import { Link } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { User, Mail, Phone, Building2, Calendar, Shield, Star, Gift, Copy, ChevronRight, Award } from 'lucide-react';

const LICENSE_LABELS = {
  cosmetology: 'Cosmetology',
  barber: 'Barber',
  nails: 'Nail Technician',
  esthetician: 'Esthetician'
};

export default function ProfilePage() {
  const { user, isSubscribed } = useAuth();

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  const copyReferralCode = () => {
    if (user?.referral_code) {
      navigator.clipboard.writeText(user.referral_code);
      toast.success('Referral code copied!');
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="profile-page">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 rounded-full bg-stone-900 flex items-center justify-center mx-auto mb-4">
            <span className="text-white font-bold text-2xl">
              {user?.full_name?.charAt(0) || 'U'}
            </span>
          </div>
          <h1 className="font-heading text-3xl font-semibold text-stone-900 mb-2">
            {user?.full_name || 'User'}
          </h1>
          <div className="flex items-center justify-center gap-2 flex-wrap">
            {user?.license_type && (
              <Badge className="bg-stone-100 text-stone-700">
                <Award className="w-3 h-3 mr-1" />
                {LICENSE_LABELS[user.license_type] || user.license_type}
              </Badge>
            )}
            {user?.is_verified && (
              <Badge className="bg-[#5F8D4E]/10 text-[#5F8D4E] hover:bg-[#5F8D4E]/20">
                <Shield className="w-3 h-3 mr-1" />
                Verified
              </Badge>
            )}
            {isSubscribed ? (
              <Badge className="bg-[#D97757]/10 text-[#D97757] hover:bg-[#D97757]/20">
                <Star className="w-3 h-3 mr-1" />
                Pro Member
              </Badge>
            ) : (
              <Badge variant="secondary">Free Trial</Badge>
            )}
          </div>
        </div>

        {/* Referral Card */}
        <Card className="border-2 border-[#5F8D4E] bg-[#5F8D4E]/5 mb-6" data-testid="referral-quick-card">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#5F8D4E]/10 flex items-center justify-center">
                  <Gift className="w-5 h-5 text-[#5F8D4E]" />
                </div>
                <div>
                  <p className="text-sm text-[#5F8D4E] font-medium">Your Referral Code</p>
                  <p className="text-lg font-bold text-stone-900 tracking-wider">{user?.referral_code || '—'}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={copyReferralCode}
                  className="h-9 px-3 rounded-full border-[#5F8D4E] text-[#5F8D4E] hover:bg-[#5F8D4E]/10"
                  data-testid="copy-referral-btn"
                >
                  <Copy className="w-4 h-4" />
                </Button>
                <Link to="/referrals">
                  <Button
                    size="sm"
                    className="h-9 px-4 rounded-full bg-[#5F8D4E] text-white hover:bg-[#4A7340]"
                  >
                    View <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </Link>
              </div>
            </div>
            {user?.referral_credits > 0 && (
              <div className="mt-3 pt-3 border-t border-[#5F8D4E]/20">
                <p className="text-sm text-[#5F8D4E]">
                  You have <strong>{user.referral_credits}</strong> credit{user.referral_credits !== 1 ? 's' : ''} to redeem!
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Profile Details */}
        <Card className="border-stone-100 shadow-sm" data-testid="profile-details-card">
          <CardHeader>
            <CardTitle className="font-heading text-xl text-stone-900">Profile Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center gap-4 p-4 rounded-xl bg-stone-50">
              <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center shadow-sm">
                <User className="w-5 h-5 text-stone-600" />
              </div>
              <div>
                <p className="text-sm text-stone-500">Full Name</p>
                <p className="font-medium text-stone-900">{user?.full_name || 'Not set'}</p>
              </div>
            </div>

            <div className="flex items-center gap-4 p-4 rounded-xl bg-stone-50">
              <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center shadow-sm">
                <Mail className="w-5 h-5 text-stone-600" />
              </div>
              <div>
                <p className="text-sm text-stone-500">Email</p>
                <p className="font-medium text-stone-900">{user?.email || 'Not set'}</p>
              </div>
            </div>

            {user?.phone && (
              <div className="flex items-center gap-4 p-4 rounded-xl bg-stone-50">
                <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center shadow-sm">
                  <Phone className="w-5 h-5 text-stone-600" />
                </div>
                <div>
                  <p className="text-sm text-stone-500">Phone</p>
                  <p className="font-medium text-stone-900">{user.phone}</p>
                </div>
              </div>
            )}

            {user?.salon_name && (
              <div className="flex items-center gap-4 p-4 rounded-xl bg-stone-50">
                <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center shadow-sm">
                  <Building2 className="w-5 h-5 text-stone-600" />
                </div>
                <div>
                  <p className="text-sm text-stone-500">Salon</p>
                  <p className="font-medium text-stone-900">{user.salon_name}</p>
                </div>
              </div>
            )}

            <div className="flex items-center gap-4 p-4 rounded-xl bg-stone-50">
              <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center shadow-sm">
                <Calendar className="w-5 h-5 text-stone-600" />
              </div>
              <div>
                <p className="text-sm text-stone-500">Member Since</p>
                <p className="font-medium text-stone-900">{formatDate(user?.created_at)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Subscription Info */}
        <Card className="border-stone-100 shadow-sm mt-6" data-testid="subscription-info-card">
          <CardHeader>
            <CardTitle className="font-heading text-xl text-stone-900">Subscription</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between p-4 rounded-xl bg-stone-50">
              <div>
                <p className="font-medium text-stone-900">
                  {isSubscribed ? 'Professional Plan' : 'No Active Subscription'}
                </p>
                <p className="text-sm text-stone-500">
                  {isSubscribed 
                    ? `Renews ${formatDate(user?.subscription_expires)}`
                    : 'Upgrade to access all features'
                  }
                </p>
              </div>
              <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                isSubscribed 
                  ? 'bg-[#5F8D4E]/10 text-[#5F8D4E]' 
                  : 'bg-stone-200 text-stone-600'
              }`}>
                {isSubscribed ? 'Active' : 'Inactive'}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
